import React from 'react';
import { Link } from 'react-router-dom';
import { PartyPopper, Calendar, Building2, Users, ArrowRight } from 'lucide-react';

const featuredEvents = [
	{
		id: 1,
		title: 'Corporate Gala',
		image: 'https://source.unsplash.com/random/300x200/?corporate,event',
		description:
			'Elegant corporate gala dinner with live entertainment and networking opportunities.',
	},
	{
		id: 2,
		title: 'Wedding Reception',
		image: 'https://source.unsplash.com/random/300x200/?wedding',
		description:
			'Beautiful wedding reception with custom decor, catering, and dance floor.',
	},
	{
		id: 3,
		title: 'Birthday Celebration',
		image: 'https://source.unsplash.com/random/300x200/?birthday,party',
		description:
			'Themed birthday party with entertainment, catering, and personalized touches.',
	},
];

const services = [
	{
		icon: <Calendar className="w-12 h-12 text-primary" />,
		title: 'Corporate Events',
		description:
			'Conferences, team building, galas, and product launches designed to impress.',
	},
	{
		icon: <PartyPopper className="w-12 h-12 text-primary" />,
		title: 'Social Gatherings',
		description:
			'Birthdays, anniversaries, and special celebrations that create lasting memories.',
	},
	{
		icon: <Building2 className="w-12 h-12 text-primary" />,
		title: 'Venue Selection',
		description:
			'Access to exclusive venues that perfectly match your event needs and vision.',
	},
	{
		icon: <Users className="w-12 h-12 text-primary" />,
		title: 'Full-Service Planning',
		description:
			'End-to-end event planning with attention to every detail for a perfect experience.',
	},
];

export default function Home() {
	return (
		<div className="w-full px-2 sm:px-4 md:px-12 xl:px-32 py-8">
			{/* Hero Section */}
			<section className="w-screen max-w-none relative bg-gray-900 text-white mb-12 -mx-2 sm:-mx-4 md:-mx-12 xl:-mx-32 rounded-none">
				<div className="absolute inset-0 bg-black/60" />
				<div className="relative z-10 flex flex-col justify-center h-[60vh] md:h-[80vh] px-8 md:px-20">
					<h1 className="text-3xl md:text-5xl font-bold mb-4">
						Creating Unforgettable Events
					</h1>
					<p className="text-lg md:text-2xl mb-8">
						From intimate gatherings to grand celebrations, we bring your vision to
						life.
					</p>
					<div className="flex gap-4">
						<Link
							to="/contact"
							className="bg-primary text-white px-6 py-3 rounded font-semibold hover:bg-primary/90 transition"
						>
							Contact Us
						</Link>
						<Link
							to="/events"
							className="border border-white text-white px-6 py-3 rounded font-semibold hover:bg-white hover:text-primary transition"
						>
							View Events
						</Link>
					</div>
				</div>
			</section>

			{/* Services Section */}
			<section className="py-12">
				<h2 className="text-2xl md:text-3xl font-bold text-center mb-6 text-primary">
					Our Services
				</h2>
				<p className="text-center mb-8 text-base md:text-lg">
					We offer comprehensive event planning and management services to make
					your special occasions truly memorable.
				</p>
				<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
					{services.map((service, i) => (
						<div
							key={i}
							className="flex flex-col items-center bg-white rounded-xl shadow p-6 text-center"
						>
							<div className="mb-4">{service.icon}</div>
							<h3 className="text-lg font-semibold mb-2 text-primary-foreground">
								{service.title}
							</h3>
							<p className="text-gray-600 text-sm">
								{service.description}
							</p>
						</div>
					))}
				</div>
			</section>

			{/* Featured Events */}
			<section className="bg-gray-100 py-12 w-screen max-w-none -mx-2 sm:-mx-4 md:-mx-12 xl:-mx-32">
				<div className="px-2 sm:px-4 md:px-12 xl:px-32">
					<h2 className="text-2xl md:text-3xl font-bold text-center mb-6 text-primary">
						Featured Events
					</h2>
					<p className="text-center mb-8 text-base md:text-lg">
						Take a look at some of our recent events and get inspired.
					</p>
					<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
						{featuredEvents.map((event) => (
							<div
								key={event.id}
								className="bg-white rounded-xl shadow overflow-hidden flex flex-col"
							>
								<img
									src={event.image}
									alt={event.title}
									className="w-full h-48 object-cover"
								/>
								<div className="p-6 flex-1 flex flex-col">
									<h3 className="text-lg font-semibold mb-2 text-primary-foreground">
										{event.title}
									</h3>
									<p className="text-gray-600 text-sm mb-4 flex-1">
										{event.description}
									</p>
								</div>
							</div>
						))}
					</div>
					<div className="flex justify-center mt-8">
						<Link
							to="/events"
							className="bg-primary text-white px-6 py-3 rounded font-semibold flex items-center gap-2 hover:bg-primary/90 transition"
						>
							View All Events{' '}
							<ArrowRight className="w-4 h-4" />
						</Link>
					</div>
				</div>
			</section>

			{/* Call to Action */}
			<section className="bg-primary text-white py-12 w-screen max-w-none -mx-2 sm:-mx-4 md:-mx-12 xl:-mx-32">
				<div className="px-2 sm:px-4 md:px-12 xl:px-32 text-center">
					<h2 className="text-2xl md:text-3xl font-bold mb-4">
						Ready to Plan Your Next Event?
					</h2>
					<p className="text-lg md:text-xl mb-8">
						Contact us today for a consultation and let us help you create an
						unforgettable experience.
					</p>
					<Link
						to="/contact"
						className="border border-white text-white px-6 py-3 rounded font-semibold hover:bg-white hover:text-primary transition"
					>
						Get in Touch
					</Link>
				</div>
			</section>
		</div>
	);
}
